import { formatDate } from '@angular/common';
import { DATE_TIME_FORMAT } from '@app/core/constants/date-time-format';

const offsetTimestampPattern = /\b(?:\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?\s+(?:Z|[+-]\d{2}:\d{2})|\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,7})?)?(?:Z|[+-]\d{2}:\d{2}))\b/g;
const dotNetOffsetTimestampPattern = /^(?<month>\d{1,2})\/(?<day>\d{1,2})\/(?<year>\d{4})\s+(?<hour>\d{1,2}):(?<minute>\d{2})(?::(?<second>\d{2})(?:\.(?<fraction>\d{1,7}))?)?\s+(?<offset>Z|[+-]\d{2}:\d{2})$/;
const isoOffsetTimestampPattern = /^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})(?::(?<second>\d{2})(?:\.(?<fraction>\d{1,7}))?)?(?<offset>Z|[+-]\d{2}:\d{2})$/;

/**
 * Replaces explicit-offset timestamps in server-provided text with browser-local date and time values.
 * Values without an offset are deliberately preserved because their time zone is ambiguous.
 */
export function formatOffsetDatesInText(message: string, locale: string): string {
  return message.replace(offsetTimestampPattern, timestamp => {
    const date = parseOffsetTimestamp(timestamp);
    return date ? formatDate(date, DATE_TIME_FORMAT, locale) : timestamp;
  });
}

function parseOffsetTimestamp(timestamp: string): Date | undefined {
  const match = dotNetOffsetTimestampPattern.exec(timestamp) ?? isoOffsetTimestampPattern.exec(timestamp);
  if (!match?.groups) {
    return undefined;
  }

  const { day, fraction, hour, minute, month, offset, second, year } = match.groups;
  const values = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second ?? '0'),
    millisecond: Number((fraction ?? '').padEnd(3, '0').slice(0, 3)),
  };

  if (!isValidDateTime(values)) {
    return undefined;
  }

  const offsetMinutes = parseOffsetMinutes(offset);
  if (offsetMinutes === undefined) {
    return undefined;
  }

  const date = new Date(Date.UTC(
    values.year,
    values.month - 1,
    values.day,
    values.hour,
    values.minute,
    values.second,
    values.millisecond,
  ) - offsetMinutes * 60_000);

  return Number.isNaN(date.getTime()) ? undefined : date;
}

function isValidDateTime({
  day,
  hour,
  millisecond,
  minute,
  month,
  second,
  year,
}: {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}): boolean {
  if (
    !Number.isInteger(year) ||
    month < 1 || month > 12 ||
    day < 1 || day > 31 ||
    hour < 0 || hour > 23 ||
    minute < 0 || minute > 59 ||
    second < 0 || second > 59 ||
    millisecond < 0 || millisecond > 999
  ) {
    return false;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
}

function parseOffsetMinutes(offset: string): number | undefined {
  if (offset === 'Z') {
    return 0;
  }

  const match = /^(?<sign>[+-])(?<hour>\d{2}):(?<minute>\d{2})$/.exec(offset);
  if (!match?.groups) {
    return undefined;
  }

  const hours = Number(match.groups['hour']);
  const minutes = Number(match.groups['minute']);
  if (hours > 23 || minutes > 59) {
    return undefined;
  }

  const totalMinutes = hours * 60 + minutes;
  return match.groups['sign'] === '+' ? totalMinutes : -totalMinutes;
}
